import {
    DataPromise,
    Destination,
    getDataLoadedState,
    getDataLoadingState,
    GraphError,
    IGraphArrayPayload,
    IGraphPayload,
} from '../context/AxiosContext'
import useEntity from '../hooks/useEntity'
import { MailFolder } from '@microsoft/microsoft-graph-types'
import { useEffect, useState } from 'react'
import useAxios from './useAxios'
import { Axios } from 'axios' // noinspection JSUnusedGlobalSymbols

const getMailFoldersDelta = (axios: Axios, nextLink?: string): Promise<Map<string, MailFolder>> => {
    return axios
        .get<IGraphArrayPayload<MailFolder>>(nextLink ?? '/mailFolders/delta', {
            baseURL: Destination.Email,
            headers: {
                SuppressErrors: 'true',
                // This endpoint does not support the $top parameter.
                // @see https://docs.microsoft.com/en-us/graph/delta-query-messages#optional-request-header
                Prefer: 'odata.maxpagesize=500',
                // Reduce the amount of data we're downloading by omitting OData metadata.
                // @see https://stackoverflow.com/a/70982860/862825
                Accept: 'application/json;odata.metadata=none',
            },
            params: { $select: 'id,displayName,parentFolderId', includeHiddenFolders: true },
        })
        .then(async response => {
            const folders = new Map<string, MailFolder>()
            response.data.value.forEach(folder => folders.set(folder.id, folder))

            if (response.data['@odata.nextLink']) {
                const nextFolders = await getMailFoldersDelta(axios, response.data['@odata.nextLink'])
                nextFolders.forEach((value, key) => folders.set(key, value))
            }

            return folders
        })
}

// noinspection JSUnusedGlobalSymbols
export default function useEmailFolders(): [DataPromise<string, undefined, GraphError>, DataPromise<Map<string, MailFolder>, undefined, GraphError>] {
    const axios = useAxios()

    const [rootFolderId] = useEntity<string, IGraphPayload<MailFolder>, GraphError>(
        () => ({ url: '/mailFolders/msgfolderroot', baseURL: Destination.Email, params: { $select: 'id' } }),
        false,
        result => result.data.id,
    )

    const [folders, setFolders] =
        useState<DataPromise<Map<string, MailFolder>, undefined, GraphError>>(getDataLoadingState<Map<string, MailFolder>, undefined, GraphError>())
    useEffect(() => {
        getMailFoldersDelta(axios).then(response => setFolders(getDataLoadedState<Map<string, MailFolder>, undefined, GraphError>(response)))
    }, [rootFolderId])

    return [rootFolderId, folders]
}
